import { monthlyCosts, startupCosts, equipmentByLifestyle, lifestyleLabels } from './toolsData';

/**
 * The Nomad Cost Index — our own compilation of what the four living formats
 * cost, assembled from the figures used across the guides and the interactive
 * tools on this site. It is a compilation, not a survey: the methodology below
 * is stated on the page so readers can judge it for themselves.
 */

export const COST_INDEX_EDITION = '2026.2';
export const COST_INDEX_UPDATED = '2026-08-20';

export type LifestyleKey = 'vanLife' | 'offGrid' | 'boatLiving' | 'camping';

export const lifestyleKeys: LifestyleKey[] = ['vanLife', 'offGrid', 'boatLiving', 'camping'];

export const lifestyleCategoryPath: Record<LifestyleKey, string> = {
  vanLife: '/category/mobile-living',
  offGrid: '/category/off-grid-homes',
  boatLiving: '/category/boat-living',
  camping: '/category/camping',
};

export const lifestyleName = lifestyleLabels;

export const monthlyTotal = (key: LifestyleKey): number =>
  monthlyCosts.reduce((sum, row) => sum + row[key], 0);

export const firstYearTotal = (key: LifestyleKey): number =>
  startupCosts[key] + monthlyTotal(key) * 12;

export const equipmentSpread = (key: LifestyleKey) => {
  const items = equipmentByLifestyle[key] || [];
  return items.reduce(
    (acc, item) => ({
      budget: acc.budget + item.budget,
      mid: acc.mid + item.mid,
      premium: acc.premium + item.premium,
    }),
    { budget: 0, mid: 0, premium: 0 }
  );
};

export const costIndexMethodology = [
  'Every figure is a mid-range monthly or one-off cost for a single person or a couple sharing one rig, expressed in USD and rounded to the nearest whole dollar.',
  'Housing covers mooring, pitch or land cost only. Loan repayments and vehicle purchase sit in the start-up column instead, because they distort a monthly comparison.',
  'Fuel and energy assume roughly 800 km of travel per month for van life and camping, and shore power plus generator top-up for boat living.',
  'Figures are reviewed twice a year against the guides they came from. The edition number changes when any line moves by more than ten per cent.',
  'This is a compilation, not a survey. Where our figure disagrees with your quote, trust your quote and treat ours as the sanity check.',
];

export const costIndexAssumptions = [
  {
    label: 'Housing line',
    detail:
      'Shows only recurring mooring, pitch or land cost. Van life registers zero here because the vehicle itself is the dwelling; the rig sits in start-up capital.',
  },
  {
    label: 'Fuel and energy',
    detail:
      'Assumes roughly 800 km of travel per month for van life and camping. Boat living uses shore power plus a small generator top-up; off-grid relies on solar with occasional generator back-up.',
  },
  {
    label: 'Start-up capital',
    detail:
      'Covers the vehicle, boat, land purchase or major conversion — the one-time outlay before the first monthly bill. It does not include financing or loan interest.',
  },
  {
    label: 'Equipment spread',
    detail:
      'Budget / mid / premium tiers are built from the same equipment lists used in the Equipment Calculator, updated against current retail averages.',
  },
  {
    label: 'Scope',
    detail:
      'Figures reflect North American and European averages in USD. Local taxes, import duties and seasonal tariffs can move the number significantly in either direction.',
  },
];

export type CostIndexSource = {
  name: string;
  publisher: string;
  url: string;
  covers: string;
};

/**
 * External reference points used to sanity-check every line in the index,
 * plus the internal tools the figures are compiled from.
 */
export const costIndexSources: CostIndexSource[] = [
  {
    name: 'Gasoline and Diesel Fuel Update',
    publisher: 'U.S. Energy Information Administration',
    url: 'https://www.eia.gov/petroleum/gasdiesel/',
    covers: 'Fuel prices behind the fuel and energy line for van life and camping.',
  },
  {
    name: 'Electric Power Monthly, average retail price',
    publisher: 'U.S. Energy Information Administration',
    url: 'https://www.eia.gov/electricity/monthly/',
    covers: 'Shore power and grid-tied energy costs for boat living and off-grid back-up.',
  },
  {
    name: 'Your Driving Costs',
    publisher: 'AAA',
    url: 'https://newsroom.aaa.com/auto/your-driving-costs/',
    covers: 'Maintenance, tyres and depreciation assumptions for the vehicle-based formats.',
  },
  {
    name: 'Consumer Expenditure Surveys',
    publisher: 'U.S. Bureau of Labor Statistics',
    url: 'https://www.bls.gov/cex/',
    covers: 'Food, insurance and household spending baselines used as a reality check.',
  },
  {
    name: 'Facts + Statistics: Auto insurance',
    publisher: 'Insurance Information Institute',
    url: 'https://www.iii.org/fact-statistic/facts-statistics-auto-insurance',
    covers: 'Insurance premiums for converted vans and towed rigs.',
  },
  {
    name: 'Cost of Living Index',
    publisher: 'Numbeo',
    url: 'https://www.numbeo.com/cost-of-living/',
    covers: 'European versus North American price gaps applied to the mid-range figures.',
  },
  {
    name: 'PVWatts Calculator',
    publisher: 'NREL',
    url: 'https://pvwatts.nrel.gov/',
    covers: 'Solar yield assumptions behind off-grid energy and battery sizing.',
  },
  {
    name: 'Boat ownership and maintenance costs',
    publisher: 'BoatUS',
    url: 'https://www.boatus.com/expert-advice/expert-advice-archive/2020/february/what-does-it-cost-to-own-a-boat',
    covers: 'Mooring, haul-out and annual maintenance ranges for liveaboard boats.',
  },
];

export const costIndexInternalSources = [
  {
    label: 'Cost comparison calculator',
    path: '/tools?tab=compare',
    detail: 'The monthly line items in the table are the same dataset the comparison tool reads.',
  },
  {
    label: 'Equipment calculator',
    path: '/tools?tab=equipment',
    detail: 'Budget, mid and premium equipment spreads come from these item lists.',
  },
  {
    label: 'Break-even calculator',
    path: '/tools?tab=breakeven',
    detail: 'Start-up capital is the input the break-even model amortises against rent.',
  },
];

export const costIndexNotes: Record<LifestyleKey, string> = {
  vanLife:
    'The zero in the housing row is the whole argument for van life and also its biggest trap. Nothing is charged for a place to sleep, but fuel, insurance and maintenance together run higher than any other format on this page.',
  offGrid:
    'Off-grid has the highest entry cost and the lowest running cost. The crossover against renting usually lands somewhere in year four, which is why the break-even tool matters more here than the monthly view.',
  boatLiving:
    'Mooring and maintenance dominate. A liveaboard berth is the single largest recurring line item on this page, and haul-out years break the monthly average badly.',
  camping:
    'Kept here as the honest baseline. Long-term camping is not a housing strategy, but it is the cheapest way to test whether you actually enjoy small-space living before spending anything serious.',
};
